import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

/* ── Modal ───────────────────────────────────────────── */
export function Modal({
  aberto,
  onFechar,
  titulo,
  descricao,
  children,
  largura = 'max-w-lg',
}: {
  aberto: boolean;
  onFechar: () => void;
  titulo: string;
  descricao?: string;
  children: ReactNode;
  largura?: string;
}) {
  useEffect(() => {
    if (!aberto) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [aberto, onFechar]);

  if (!aberto) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-ink-950/50 backdrop-blur-sm animate-fade-in"
        onClick={onFechar}
      />
      <div
        className={`relative w-full ${largura} bg-white rounded-2xl shadow-modal animate-fade-up max-h-[90vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-ink-100">
          <div>
            <h2 className="font-display text-lg font-bold text-ink-950 tracking-tight">{titulo}</h2>
            {descricao && <p className="text-[13px] text-ink-500 mt-0.5">{descricao}</p>}
          </div>
          <button onClick={onFechar} className="icon-btn -mr-2 -mt-1" aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

/* ── Cabeçalho de página ─────────────────────────────── */
export function PageHeader({
  titulo,
  descricao,
  children,
}: {
  titulo: string;
  descricao?: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-950 tracking-tight">{titulo}</h1>
        {descricao && <p className="text-sm text-ink-500 mt-1">{descricao}</p>}
      </div>
      {children && <div className="flex items-center gap-3 no-print">{children}</div>}
    </div>
  );
}

/* ── Spinner ─────────────────────────────────────────── */
export function Spinner({ className = 'h-8 w-8' }: { className?: string }) {
  return (
    <div className={`${className} rounded-full border-[3px] border-brand-100 border-t-brand-600 animate-spin`} />
  );
}

export function Loading({ altura = 'h-48' }: { altura?: string }) {
  return (
    <div className={`flex items-center justify-center ${altura}`}>
      <Spinner />
    </div>
  );
}

/* ── Estado vazio ────────────────────────────────────── */
export function EmptyState({
  icon: Icon,
  titulo,
  descricao,
}: {
  icon: any;
  titulo: string;
  descricao?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center animate-fade-in">
      <div className="w-12 h-12 rounded-xl bg-ink-100 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-ink-400" />
      </div>
      <p className="text-sm font-semibold text-ink-700">{titulo}</p>
      {descricao && <p className="text-sm text-ink-400 mt-1 max-w-xs">{descricao}</p>}
    </div>
  );
}

/* ── Card de métrica (KPI) ───────────────────────────── */
const kpiTons: Record<string, { icon: string; ring: string }> = {
  brand: { icon: 'bg-brand-50 text-brand-600', ring: 'group-hover:ring-brand-200' },
  amber: { icon: 'bg-amber-50 text-amber-600', ring: 'group-hover:ring-amber-200' },
  blue: { icon: 'bg-blue-50 text-blue-600', ring: 'group-hover:ring-blue-200' },
  green: { icon: 'bg-emerald-50 text-emerald-600', ring: 'group-hover:ring-emerald-200' },
  red: { icon: 'bg-red-50 text-red-600', ring: 'group-hover:ring-red-200' },
  purple: { icon: 'bg-violet-50 text-violet-600', ring: 'group-hover:ring-violet-200' },
};

export function KpiCard({
  titulo,
  valor,
  icon: Icon,
  tom = 'brand',
  detalhe,
}: {
  titulo: string;
  valor: ReactNode;
  icon: any;
  tom?: keyof typeof kpiTons | string;
  detalhe?: string;
}) {
  const t = kpiTons[tom as string] || kpiTons.brand;
  return (
    <div className={`group card p-5 transition-all duration-200 hover:shadow-pop hover:-translate-y-0.5 ring-1 ring-transparent ${t.ring}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[13px] font-medium text-ink-500">{titulo}</p>
          <p className="font-display text-[28px] leading-tight font-bold text-ink-950 mt-1.5 tracking-tight">{valor}</p>
          {detalhe && <p className="text-xs text-ink-400 mt-1">{detalhe}</p>}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${t.icon}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

/* ── Badges de domínio ───────────────────────────────── */
const badgeTons: Record<string, string> = {
  amber: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-600/20',
  blue: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20',
  green: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20',
  red: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-600/20',
  gray: 'bg-ink-100 text-ink-600 ring-1 ring-inset ring-ink-400/20',
  brand: 'bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/20',
};
const dotTons: Record<string, string> = {
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
  green: 'bg-emerald-500',
  red: 'bg-red-500',
  gray: 'bg-ink-400',
  brand: 'bg-brand-500',
};

export function Badge({ tom = 'gray', label }: { tom?: string; label: string }) {
  return (
    <span className={`badge ${badgeTons[tom] || badgeTons.gray}`}>
      <span className={`badge-dot ${dotTons[tom] || dotTons.gray}`} />
      {label}
    </span>
  );
}

const situacaoPedidoMap: Record<string, { tom: string; label: string }> = {
  AGUARDANDO_ENTRADA: { tom: 'amber', label: 'Aguardando' },
  EM_PRODUCAO: { tom: 'blue', label: 'Em Produção' },
  PRONTO: { tom: 'green', label: 'Pronto' },
  ENTREGUE: { tom: 'gray', label: 'Entregue' },
  CANCELADO: { tom: 'red', label: 'Cancelado' },
};

export function BadgeSituacao({ situacao }: { situacao: string }) {
  const s = situacaoPedidoMap[situacao] || { tom: 'gray', label: situacao };
  return <Badge tom={s.tom} label={s.label} />;
}

const pagamentoMap: Record<string, { tom: string; label: string }> = {
  PENDENTE: { tom: 'amber', label: 'Pendente' },
  PARCIAL: { tom: 'blue', label: 'Parcial' },
  QUITADO: { tom: 'green', label: 'Quitado' },
};

export function BadgePagamento({ situacao }: { situacao: string }) {
  const s = pagamentoMap[situacao] || { tom: 'gray', label: situacao };
  return <Badge tom={s.tom} label={s.label} />;
}

const prazoMap: Record<string, { tom: string; label: string }> = {
  URGENTE: { tom: 'red', label: 'Urgente' },
  PRAZO_CURTO: { tom: 'amber', label: 'Curto' },
  PRAZO_CONFORTAVEL: { tom: 'green', label: 'Confortável' },
};

export function BadgePrazo({ tipo }: { tipo: string }) {
  const s = prazoMap[tipo] || { tom: 'gray', label: tipo };
  return <Badge tom={s.tom} label={s.label} />;
}

const situacaoLoteMap: Record<string, { tom: string; label: string }> = {
  ENVIADO: { tom: 'blue', label: 'Enviado' },
  PARCIAL: { tom: 'amber', label: 'Parcial' },
  RECEBIDO: { tom: 'green', label: 'Recebido' },
  PAGO: { tom: 'gray', label: 'Pago' },
};

export function BadgeLote({ situacao }: { situacao: string }) {
  const s = situacaoLoteMap[situacao] || { tom: 'gray', label: situacao };
  return <Badge tom={s.tom} label={s.label} />;
}

/* ── Logotipo ────────────────────────────────────────── */
export function LogoMark({ size = 36 }: { size?: number }) {
  return (
    <div
      className="rounded-xl bg-white ring-1 ring-ink-200/70 flex items-center justify-center shadow-sm shrink-0 overflow-hidden"
      style={{ width: size, height: size }}
    >
      <img
        src="/brand/logo-icon.png"
        alt="Monchery Malharia"
        style={{ width: size * 0.82, height: size * 0.82 }}
        className="object-contain"
        draggable={false}
      />
    </div>
  );
}
